import { Component, EventEmitter, Input, NO_ERRORS_SCHEMA, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CheckBoxModule, InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import {
  DialogCloseResult,
  DialogContentBase,
  DialogRef,
  DialogService,
  DialogsModule,
  WindowModule,
  WindowRef
} from '@progress/kendo-angular-dialog';
import { NotificationModule, NotificationService } from '@progress/kendo-angular-notification';
import { KENDO_PAGER, PagerModule } from '@progress/kendo-angular-pager';
import { GridModule, PageChangeEvent, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { MulticheckFilterComponent } from '../../../filters/multicheck-filter/multicheck-filter.component';
import { Observable, Subscription } from 'rxjs';
import { CasiCloseOptions, FormModes } from '../../../common/constants.lib';
import { LoggerService } from '../../../shared/logger.service';
import { MeService } from '../../../auth/me.service';
import { ToastrService } from 'ngx-toastr';
import { faArrowDown, faArrowUp, faEdit, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { setTitle } from '../../../common/utils.lib';
import { DeleteConfirmComponent } from '../../../common-components/delete-confirm/delete-confirm.component';
import { ErrorDialogModalComponent } from '../../../common-components/error-dialog-modal/error-dialog-modal.component';
import {
  OrganisationDiscipline,
  OrganisationDisciplineUser, OrganisationDisciplineUserNew,
  OrganisationDisciplineUserType
} from '../../../models/models.lib';
import { CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { OrganisationDisciplineUserService } from '../../organisation-discipline-user.service';
import {
  OrganisationDisciplineUserAddComponent
} from '../organisation-discipline-user-add.component/organisation-discipline-user-add.component';

@Component({
  selector: 'casi-organisation-discipline-users-win',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule, ReactiveFormsModule,
    FontAwesomeModule,
    InputsModule,
    ButtonsModule,
    DialogsModule,
    WindowModule,
    NotificationModule,
    CheckBoxModule,
    KENDO_PAGER,
    PagerModule,
    GridModule,
    MulticheckFilterComponent
  ],
  // schemas: [NO_ERRORS_SCHEMA],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './organisation-discipline-users-win.html',
  styleUrl: './organisation-discipline-users-win.scss'
})
export class OrganisationDisciplineUsersWin implements OnInit, OnDestroy {
  private _organisationDiscipline: OrganisationDiscipline | undefined;

  @Input({ required: true }) set organisationDiscipline(val: OrganisationDiscipline) {
    this._organisationDiscipline = val;
  }
  get organisationDiscipline(): OrganisationDiscipline | undefined {
    return this._organisationDiscipline;
  };

  @Output() onCancel = new EventEmitter<void>();

  private _windowRef: WindowRef | undefined;

  public view: Observable<OrganisationDisciplineUser[]> = new Observable<OrganisationDisciplineUser[]>();
  public rawData: OrganisationDisciplineUser[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: OrganisationDisciplineUser[] = filterBy(this.rawData, this.filter);

  public currentItem: OrganisationDisciplineUserType = OrganisationDisciplineUserNew.create(this.organisationDiscipline);

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";

  public pageSize = 10;

  private _subs: Subscription[] = [];
  public formMode: FormModes = FormModes.NOTSET;

  constructor(
    private logger: LoggerService,
    private notificationService: NotificationService,
    private me: MeService,
    public dataService: OrganisationDisciplineUserService,
    public faIcons: FaIconLibrary,
    private dialogService: DialogService,
    private toastr: ToastrService
  ) {
    faIcons.addIcons(
      faPlusCircle, faEdit, faArrowUp, faArrowDown
    );
  }

  public sort: SortDescriptor[] = [
    { field: "discipline_name", dir: "asc" },
  ];

  //#region Ng Event handlers

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    if (this.organisationDiscipline)
      this.dataService.query(this.organisationDiscipline.id);

    this._subs.push(this.dataService.subscribe(e => {
      this.logger.debug('ngOnInit:v: ', e);
      this.rawData = e;
      this.loadData();
    }));
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  //#endregion

  //#region Grid functions

  public pageChange(event: PageChangeEvent): void {
    this.logger.debug(`pageChange: `, event);
  }

  onClearFilters(gridName: string) {
    this.logger.debug("onClearFilters: ", gridName);
    this.filter = { logic: "and", filters: [] };
    this.loadData();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): any[] {
    this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]);
  }

  //#endregion

  //#region Public functions

  public onCancelAction(): void {
    this.logger.debug(`onCancelAction...`);
    this.onCancel.emit();
    // this.dialog.close({ text: CasiCloseOptions.Cancelled });
  }

  onConfirmDelete(dataItem: any): void {
    this.logger.debug("onConfirmDelete: ", dataItem);
    this.currentItem = OrganisationDisciplineUser.create(dataItem);
    this.openConfirmDailog();
  }

  public onAddClick(): void {
    this.logger.debug("onAdd...");
    this.currentItem = OrganisationDisciplineUserNew.create(this.organisationDiscipline);
    this.formMode = FormModes.NEW;
    this.logger.debug("onAdd:currentItem ", this.currentItem);
    this.openAddEditDialog(this.formMode, this.currentItem);
  }

  onEditClick(dataItem: any): void {
    this.logger.debug("onEditClick: ", dataItem);
    this.currentItem = OrganisationDisciplineUser.create(dataItem);
    this.formMode = FormModes.EDIT;
    this.logger.debug("onEditClick:currentItem ", this.currentItem);
    this.openAddEditDialog(this.formMode, this.currentItem);
  }

  //#endregion

  //#region Private functions

  private openAddEditDialog(mode: FormModes, dataItem: OrganisationDisciplineUserType): void {
    this.logger.debug("openAddEditDialog...");
    const dialogRef = this.dialogService.open({
      content: OrganisationDisciplineUserAddComponent,
      minWidth: 320,
      width: 600,
    });

    const contentRef = dialogRef.content.instance as OrganisationDisciplineUserAddComponent;
    contentRef.title = setTitle(mode, 'ORGANISATION DISCIPLINE USER');
    contentRef.organisationDisciplineUser = dataItem;
    contentRef.mode = mode;
    // contentRef.selectedRoleIds = dataItem.roles;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openAddEditDialog:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Saved) {
        this.logger.debug("openAddEditDialog:saved... ", contentRef.editForm.value);
        this.handleClose(contentRef.editForm.value, mode);
      }
    });
  }

  private handleClose(data: any, mode: FormModes): void {
    this.logger.debug('handleClose...', data);

    let dup: boolean = false;
    if (data) {
      let inUse = this.rawData.find(r => r.email === data.email);
      this.logger.debug('handleClose...inUse', inUse);
      if (inUse) {
        if (mode === FormModes.NEW) {
          dup = true;
        } else {
          dup = inUse.id !== data.id;
        }
      }
      if (dup) {
        this.logger.warn(`handleClose: discipline user: ${data.email} already in use `);
        this.openErrorDailog("Duplicate Organisation User Email", "That Organisation Discipline User already exists!", "Close and Retry")
      } else
        this.save(data, mode);
    }
  }

  private save(organisationDisciplineUser: any, mode: FormModes): void {
    this.logger.debug('saveDiscipline...', organisationDisciplineUser, mode);
    organisationDisciplineUser.user_roles = organisationDisciplineUser.user_roles ? organisationDisciplineUser.user_roles.map((d: any) => d.id) : [];
    if (mode === FormModes.EDIT) {
      this.dataService.update(organisationDisciplineUser.id, organisationDisciplineUser)
        .then(resp => {
          this.logger.debug('saveDiscipline:update:resp: ', resp);
          this.notify("Organisation Discipline User Updated");
          this.closeWindowRef();
          if (this.organisationDiscipline)
            this.dataService.query(this.organisationDiscipline.id);
        })
        .catch(err => {
          this.logger.error('saveDiscipline:update:err: ', err);
          this.notify("Error Updating Organisation Discipline User", true);
        });
    } else {
      this.dataService.add(organisationDisciplineUser)
        .then(resp => {
          this.logger.debug('saveDiscipline:add:resp: ', resp);
          this.notify("Organisation Discipline User Added");
          this.closeWindowRef();
          if (this.organisationDiscipline)
            this.dataService.query(this.organisationDiscipline.id);
        })
        .catch(err => {
          this.logger.error('saveDiscipline:add:err: ', err);
          this.notify("Error Creating Organisation User", true);
        });
    }
  }


  private openConfirmDailog(): void {
    this.logger.debug("openConfirmDailog: ", this.currentItem);
    const dialogRef = this.dialogService.open({
      content: DeleteConfirmComponent,
      minWidth: 250,
      width: 450
    });

    const contentRef = dialogRef.content.instance as DeleteConfirmComponent;
    contentRef.title = "CONFIRM DELETE";
    contentRef.content = `Delete User - <b>${this.currentItem?.email}</b>?`;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openDialogForm:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Ok) {
        this.logger.debug("openConfirmDailog:ok to proceed... ", this.currentItem);
        this.destroy(this.currentItem);
      }
    });
  }

  private destroy(organisationDisciplineUser: any): void {
    this.logger.debug('save...', organisationDisciplineUser);
    this.dataService.destroy(organisationDisciplineUser.id)
      .then(resp => {
        this.logger.debug('save:update:resp: ', resp);
        this.notify("Organisation Discipline User Deleted");
        this.closeWindowRef();
        if (this._organisationDiscipline)
          this.dataService.query(this._organisationDiscipline.id);
      })
      .catch(err => {
        this.logger.error('saveDiscipline:update:err: ', err);
        this.notify("Error Deleting Organisation Discipline User", true);
      });
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  private openErrorDailog(title: string = 'Not set', content: string = 'No content', buttonText: string = 'Close'): void {
    const dialogRef = this.dialogService.open({
      content: ErrorDialogModalComponent,
      minWidth: 320,
      width: 750,
      cssClass: "custom-css-class"
    });

    const contentRef = dialogRef.content.instance as ErrorDialogModalComponent;
    contentRef.title = title;
    contentRef.content = content;
    contentRef.buttonText = buttonText;
  }

  private closeWindowRef(): void {
    if (this._windowRef) {
      this._windowRef.close();
      this._windowRef = undefined;
    }
    // this.editWinOpen = false;
  }

  private loadData(): void {
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
  }

  //#endregion

}

